import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFDropdown from 'components/HFInputs/HFDropdown'
import HFCheckbox from 'components/HFInputs/HFCheckbox'
import HFValidatePostalCodeInput from 'components/HFInputs/HFValidatePostalCodeInput'
import HFOptionGroup from 'components/HFInputs/HFOptionGroup'
import AlertBox from 'components/AlertBox/AlertBox'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { booleanWithNoInfoOptions } from 'controllers/TaskManager/formOptions'
import { Segment } from 'utils/enums'

import styles from '../../Dialog.module.sass'

/**
 * Initial assessment block of the forwarding form.
 *
 * Base questions (always visible): body part (with a "No information" toggle)
 * and postal code. Questions 3 and 4 (central provider / ITA) are only shown
 * once the body part is chosen, the postal code is validated, and defaults are
 * not being forced (see `showExtraQuestions` in the parent).
 */
export const InitialAssessment = ({
	bodyParts,
	bodyPartNoInfo,
	bodyPartForcesDefaults,
	postalCodeNoInfo,
	showExtraQuestions,
	postalMissingCoordinates,
	postalIsRegional,
	postalCentralWithin25km,
	isCheckingPostal,
	accidentType,
	disabled = false,
}) => {
	const { i18n } = useGlobal()

	return (
		<fieldset className={disabled ? styles['locked-section'] : undefined}>
			<h4>{i18n`Initial assessment`}</h4>
			<VStack className={styles['option-group-list']}>
				{/* 1. Parte do corpo */}
				<VStack className={styles['form-input']}>
					<h5>{i18n`Body part`}</h5>
					<HFDropdown
						className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
						optionsAlign='right'
						searchable={true}
						name='bodyPart.bodyPartName'
						noData={i18n`No results`}
						options={bodyParts || []}
						type='number'
						disabled={disabled || bodyPartNoInfo}
					/>
					<HFCheckbox
						className={styles['checkbox']}
						name='bodyPart.noInfo'
						label={i18n`No information`}
						disabled={disabled}
					/>

					{/* Info: eyes/teeth force the central provider (central
					    question is not asked). */}
					{bodyPartForcesDefaults && (
						<small className={styles['postal-info-hint']}>
							{i18n`Central provider assumed for this body part`}
						</small>
					)}
				</VStack>

				{/* 2. Codigo Postal */}
				<VStack className={styles['form-input']}>
					<h5>{i18n`Postal code`}</h5>
					<HFValidatePostalCodeInput
						name='dataValidation.postalCode'
						type='text'
						inputMask='9999-999'
						placeholder='____-___'
						disabled={disabled || postalCodeNoInfo}
					/>
					<HFCheckbox
						className={styles['checkbox']}
						name='dataValidation.postalCodeNoInfo'
						label={i18n`No information`}
						disabled={disabled}
					/>
					{postalMissingCoordinates && (
						<AlertBox
							className={styles['alert-box']}
							type='warning'
							items={[
								{
									title: i18n`Warning`,
									text: i18n`This postal code has no coordinates associated`,
								},
							]}
						/>
					)}

					{/* Info: what the postal code check inferred (central provider
					    is assumed, so the central question is not asked). Skipped
					    when the body part (eyes/teeth) already forces the central
					    provider — the postal result is then irrelevant. */}
					{!bodyPartForcesDefaults &&
						(postalIsRegional || postalCentralWithin25km) && (
							<small className={styles['postal-info-hint']}>
								{postalIsRegional
									? i18n`Regional postal code — central provider assumed`
									: i18n`Central provider within 25 km — central provider assumed`}
							</small>
						)}
				</VStack>

				{isCheckingPostal && (
					<LoadingIndicator label={i18n`Checking postal code`} />
				)}

				{/* Sports accidents (AD) also collect the injured's email here. */}
				{accidentType === Segment.SportsAccident && (
					<VStack className={styles['form-input']}>
						<h5>{i18n`Injured Email`}</h5>
						<HFTextInput
							name='dataValidation.email'
							multiline={false}
							rows={5}
							noResize
							disabled={disabled}
						/>
					</VStack>
				)}

				{/* Q3 only shows after body part + validated postal, unless
				    defaults are forced (eyes/teeth or regional/central postal).
				    ITA ("had the need to stop working") was removed from the
				    initial form — it is always assumed "No" (see the model hook). */}
				{showExtraQuestions && (
					<VStack>
						<h5>{i18n`Needs central provider`}</h5>
						<HFOptionGroup
							className={`${styles['option-group']} ${styles['--horizontal-flex-wide']}`}
							name='accident.isSerious'
							type='boolean'
							options={booleanWithNoInfoOptions}
							disabled={disabled}
							// Never drop the answer when this question is hidden
							shouldUnregister={false}
						/>
					</VStack>
				)}
			</VStack>
		</fieldset>
	)
}
